<script setup lang="ts">
/**
 * 美国地图组件 - 使用ECharts实现真实地图
 * 默认所有州显示为灰色，父组件传入需要高亮的州列表
 */
import type { EChartsOption } from 'echarts'
import { useEcharts } from '~/composables/useEcharts'
import usaMapData from '~/assets/json/us-states.json'

const MAP_ICON = 'https://resources1.interface003.com/web30/2026/07/10/73751532-ae71-419c-9389-e9700b819ff7.png'
const MAP_NAME = 'USA_LICENSE'
const HIGHLIGHTED_HOVER_COLOR = '#AFC5FA'

// 将阿拉斯加、夏威夷放入本土地图左下方的空白区域。
const relocatedMapData = JSON.parse(JSON.stringify(usaMapData)) as typeof usaMapData
relocatedMapData.features.push({
  type: 'Feature',
  id: 'PR',
  properties: { name: 'Puerto Rico' },
  geometry: {
    type: 'Polygon',
    coordinates: [
      [
        [-66.448338, 17.984326],
        [-66.771478, 18.006234],
        [-66.924832, 17.929556],
        [-66.985078, 17.973372],
        [-67.209633, 17.956941],
        [-67.154863, 18.19245],
        [-67.269879, 18.362235],
        [-67.094617, 18.515589],
        [-66.957694, 18.488204],
        [-66.409999, 18.488204],
        [-65.840398, 18.433435],
        [-65.632274, 18.367712],
        [-65.626797, 18.203403],
        [-65.730859, 18.186973],
        [-65.834921, 18.017187],
        [-66.234737, 17.929556],
        [-66.448338, 17.984326],
      ],
    ],
  },
} as (typeof usaMapData.features)[number])
const relocatedStateConfig: Record<string, { center: [number, number]; scale: number }> = {
  Alaska: { center: [-119, 25], scale: 0.35 },
  Hawaii: { center: [-105, 25], scale: 1 },
  'Puerto Rico': { center: [-91, 25], scale: 0.7 },
}

const getGeometryPoints = (coordinates: unknown): number[][] => {
  const points: number[][] = []
  const collect = (value: unknown): void => {
    if (!Array.isArray(value)) return
    if (typeof value[0] === 'number') {
      points.push(value as number[])
      return
    }
    value.forEach(collect)
  }
  collect(coordinates)
  return points
}

for (const feature of relocatedMapData.features) {
  const config = relocatedStateConfig[feature.properties.name]
  if (!config) continue

  const points = getGeometryPoints(feature.geometry.coordinates)
  const normalizedPoints = points.map(([longitude, latitude]) => [
    feature.properties.name === 'Alaska' && longitude > 0 ? longitude - 360 : longitude,
    latitude,
  ])
  const sourceCenter: [number, number] = [
    (Math.min(...normalizedPoints.map(([longitude]) => longitude)) +
      Math.max(...normalizedPoints.map(([longitude]) => longitude))) /
      2,
    (Math.min(...normalizedPoints.map(([, latitude]) => latitude)) +
      Math.max(...normalizedPoints.map(([, latitude]) => latitude))) /
      2,
  ]
  let pointIndex = 0
  const relocate = (value: unknown): unknown => {
    if (!Array.isArray(value)) return value
    if (typeof value[0] === 'number') {
      const [longitude, latitude] = normalizedPoints[pointIndex++]
      return [
        config.center[0] + (longitude - sourceCenter[0]) * config.scale,
        config.center[1] + (latitude - sourceCenter[1]) * config.scale,
      ]
    }
    return value.map(relocate)
  }
  feature.geometry.coordinates = relocate(feature.geometry.coordinates) as typeof feature.geometry.coordinates
}
// 州数据类型定义
export interface StateData {
  /** 州代码，如 'WA', 'CA', 'NY' */
  code: string
  /** 州名称，如 'Washington', 'California' */
  name: string
}

const props = withDefaults(
  defineProps<{
    /** 需要高亮显示的州列表 */
    highlightedStates: StateData[]
    /** 地图容器宽度，避免 popover 内 100% 拿不到尺寸 */
    width?: number
    /** 地图容器高度 */
    height?: number
  }>(),
  { width: 412, height: 306 },
)

// 美国各州代码到完整州名的映射（匹配GeoJSON中的feature name）
const stateNameMap: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}
// 计算每个州 GeoJSON 的几何中心
const stateCentroids = computed(() => {
  const result: Record<string, [number, number]> = {}
  for (const feat of (relocatedMapData as any).features) {
    const name = feat.properties?.name as string
    const coords = feat.geometry.coordinates
    const pts: [number, number][] = []
    const collect = (arr: unknown): void => {
      const a = arr as unknown[]
      if (typeof a[0] === 'number') {
        pts.push([a[0] as number, a[1] as number])
      } else {
        for (const sub of a) collect(sub)
      }
    }
    if (feat.geometry.type === 'Polygon') {
      for (const poly of coords) collect(poly)
    } else if (feat.geometry.type === 'MultiPolygon') {
      for (const mp of coords) for (const poly of mp) collect(poly)
    }
    if (pts.length) {
      result[name] = [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length]
    }
  }
  return result
})

// 高亮州数量
const highlightedCount = computed(() => props.highlightedStates.length)

// 容器内联样式，用固定宽高确保 ECharts init 时尺寸正确
const chartStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
}))

// 图表容器引用和实例
const mapChartRef = ref<HTMLElement | null>(null)
const hoveredIconIndex = ref<number | null>(null)
const hoveredStateName = ref<string | null>(null)
const iconSizes = ref<Record<number, number>>({})
let mapChart: ReturnType<typeof init> | null = null
let resizeHandler: (() => void) | null = null
let resizeObserver: ResizeObserver | null = null
let iconAnimationFrame: number | null = null

// 判断当前区域是否为高亮州，仅高亮州区域允许显示浮窗
const isHighlightedState = (name: string) =>
  props.highlightedStates.some((state) => (stateNameMap[state.code] || state.name) === name)

// 获取高亮州对应的 Icon 下标，保持区域和 Icon 共用同一个 Hover 状态
const getIconIndexByName = (name: string) =>
  props.highlightedStates
    .filter((s) => stateCentroids.value[stateNameMap[s.code] || s.name])
    .findIndex((s) => (stateNameMap[s.code] || s.name) === name)

// 根据当前尺寸生成 Icon 数据，保证 pin 底部尖角始终固定在地理坐标
const buildIconData = () =>
  props.highlightedStates
    .filter((s) => stateCentroids.value[stateNameMap[s.code] || s.name])
    .map((s, index) => {
      const name = stateNameMap[s.code] || s.name
      const size = iconSizes.value[index] ?? 29
      return {
        name,
        value: stateCentroids.value[name],
        symbolSize: size,
        symbolOffset: [0, -size / 2],
      }
    })

// 构建ECharts配置项
const buildOption = (): EChartsOption => ({
  tooltip: {
    trigger: 'item',
    formatter: (params: any) => (isHighlightedState(params.name) ? params.name : ''),
    position: (
      point: number[],
      params: any,
      _dom: HTMLElement,
      _rect: { x: number; y: number; width: number; height: number } | undefined,
      size: { contentSize: number[] },
    ) => {
      // tooltip 由 geo region 触发（scatter silent），point 是鼠标位置而非 icon 中心
      // 用 convertToPixel 将州中心地理坐标转为像素坐标，精确定位 icon
      const centroid = stateCentroids.value[params.name]
      if (!centroid || !mapChart) return point
      const pixelPos = mapChart.convertToPixel({ geoIndex: 0 }, centroid)
      if (!pixelPos) return point

      const iconIndex = getIconIndexByName(params.name)
      const currentSize = iconIndex >= 0 ? (iconSizes.value[iconIndex] ?? 29) : 29

      // icon 通过 symbolOffset: [0, -size/2] 向上偏移，顶部 = pixelPos[1] - 当前尺寸
      // 浮窗底部（含 7px 箭头）对齐 icon 顶部
      const x = pixelPos[0] - size.contentSize[0] / 2
      const y = pixelPos[1] - currentSize - size.contentSize[1] - 10
      return [x, y]
    },
    className: 'usa-map-tooltip',
    backgroundColor: '#fff',
    borderColor: 'rgba(0,0,0,0.15)',
    borderWidth: 0,
    textStyle: { color: '#292A2E', fontFamily: 'Outfit', fontSize: 14, fontWeight: 500 },
    extraCssText:
      'box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 8px; padding: 8px 12px; font-family: Outfit; color: #292A2E;',
  },
  geo: {
    map: MAP_NAME,
    roam: false,
    center: [-96, 36],
    zoom: 1.2,
    silent: false,
    tooltip: {
      show: false,
    },
    label: {
      show: false,
    },
    itemStyle: {
      areaColor: '#F0F1F2',
      borderColor: '#fff',
      borderWidth: 0.8,
    },
    emphasis: {
      label: {
        show: false,
      },
      itemStyle: {
        areaColor: '#0000001A',
        borderColor: '#fff',
        borderWidth: 1,
      },
    },
    regions: props.highlightedStates.map((s) => ({
      name: stateNameMap[s.code] || s.name,
      tooltip: {
        show: true,
      },
      label: {
        show: false,
      },
      itemStyle: {
        areaColor: hoveredStateName.value === (stateNameMap[s.code] || s.name) ? HIGHLIGHTED_HOVER_COLOR : '#CFE1FD',
      },
      emphasis: {
        label: {
          show: false,
        },
        itemStyle: { areaColor: HIGHLIGHTED_HOVER_COLOR },
      },
    })),
  },
  series: [
    // 高亮州叠加 icon
    {
      type: 'scatter',
      coordinateSystem: 'geo',
      symbol: 'image://' + MAP_ICON,
      silent: true,
      z: 10,
      tooltip: {
        formatter: (params: any) => (isHighlightedState(params.name) ? params.name : ''),
      },
      data: buildIconData(),
    },
  ],
})

// 逐帧更新 Icon 尺寸，Canvas 图表的 symbolSize 不会自动产生 CSS 过渡
const animateIconSize = (dataIndex: number, targetSize: number) => {
  if (iconAnimationFrame !== null) {
    cancelAnimationFrame(iconAnimationFrame)
  }

  const startSize = iconSizes.value[dataIndex] ?? 29
  const startTime = performance.now()
  const duration = 50

  const update = (currentTime: number) => {
    const progress = Math.min((currentTime - startTime) / duration, 1)
    const easedProgress = 1 - (1 - progress) ** 3
    iconSizes.value[dataIndex] = startSize + (targetSize - startSize) * easedProgress
    mapChart?.setOption({
      series: [
        {
          data: buildIconData(),
        },
      ],
    })

    if (progress < 1) {
      iconAnimationFrame = requestAnimationFrame(update)
    } else {
      iconAnimationFrame = null
    }
  }

  iconAnimationFrame = requestAnimationFrame(update)
}

// 地图区域和 Icon 共用同一个 Hover 状态
const handleMapMouseover = (params: any) => {
  if (params.componentType !== 'geo' || !isHighlightedState(params.name)) return
  const dataIndex = getIconIndexByName(params.name)
  if (dataIndex < 0 || hoveredIconIndex.value === dataIndex) return
  hoveredStateName.value = params.name
  mapChart?.setOption(buildOption())
  if (hoveredIconIndex.value !== null) {
    iconSizes.value[hoveredIconIndex.value] = 29
  }
  hoveredIconIndex.value = dataIndex
  animateIconSize(dataIndex, 50)
}

// 鼠标移出地图区域后恢复 Icon 尺寸
const handleMapMouseout = (params: any) => {
  if (params.componentType !== 'geo') return
  const dataIndex = getIconIndexByName(params.name)
  if (dataIndex < 0 || hoveredIconIndex.value !== dataIndex) return
  hoveredStateName.value = null
  mapChart?.setOption(buildOption())
  hoveredIconIndex.value = null
  if (iconAnimationFrame !== null) {
    cancelAnimationFrame(iconAnimationFrame)
    iconAnimationFrame = null
  }
  iconSizes.value[dataIndex] = 29
  mapChart?.setOption({
    series: [
      {
        data: buildIconData(),
      },
    ],
  })
}

// 图表整体失去鼠标焦点时兜底恢复当前 Icon
const handleChartGlobalout = () => {
  if (hoveredIconIndex.value === null) return
  const dataIndex = hoveredIconIndex.value
  hoveredStateName.value = null
  mapChart?.setOption(buildOption())
  hoveredIconIndex.value = null
  if (iconAnimationFrame !== null) {
    cancelAnimationFrame(iconAnimationFrame)
    iconAnimationFrame = null
  }
  iconSizes.value[dataIndex] = 29
  mapChart?.setOption({
    series: [
      {
        data: buildIconData(),
      },
    ],
  })
}

// 初始化图表
const { init, load } = useEcharts()

const initChart = async () => {
  if (!import.meta.client || !mapChartRef.value) return

  // 预注册重定位后的美国地图。
  const echarts = await load()
  if (!echarts.getMap(MAP_NAME)) {
    echarts.registerMap(MAP_NAME, relocatedMapData as any)
  }

  mapChart = init(mapChartRef.value, { lazy: true, autoResize: false, renderer: 'canvas' })

  void (async () => {
    const chart = await mapChart?.ready()
    if (!chart) return

    chart.setOption(buildOption(), true)
    chart.on('mouseover', handleMapMouseover)
    chart.on('mouseout', handleMapMouseout)
    chart.on('globalout', handleChartGlobalout)
    chart.resize()

    // popover 展开时容器从 0 变为实际尺寸，window resize 不触发，用 ResizeObserver 兜底
    resizeObserver = new ResizeObserver(() => {
      chart.resize()
      chart.setOption(buildOption(), true)
    })
    if (mapChartRef.value) {
      resizeObserver.observe(mapChartRef.value)
    }

    resizeHandler = () => chart.resize()
    window.addEventListener('resize', resizeHandler)
  })().catch(() => undefined)
}
// 监听props变化更新图表
watch(
  () => props.highlightedStates,
  () => {
    if (mapChart) {
      mapChart.setOption(buildOption(), true)
    }
  },
  { deep: true },
)

onMounted(() => {
  nextTick(() => {
    void initChart()
  })
})

onBeforeUnmount(() => {
  if (iconAnimationFrame !== null) {
    cancelAnimationFrame(iconAnimationFrame)
    iconAnimationFrame = null
  }
  mapChart?.off('mouseover', handleMapMouseover)
  mapChart?.off('mouseout', handleMapMouseout)
  mapChart?.off('globalout', handleChartGlobalout)
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  mapChart?.dispose()
  mapChart = null
})
</script>

<template>
  <div class="map-container">
    <div ref="mapChartRef" class="map-chart" :style="chartStyle"></div>
    <div class="state-count">{{ highlightedCount }} states</div>
  </div>
</template>

<style scoped lang="less">
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #fff;
}

.map-chart {
  /* 尺寸由 props 通过内联 style 控制 */
}

.state-count {
  position: absolute;
  right: 0px;
  bottom: 0px;
  padding: 2px 8px;
  border-radius: 2px;
  background: rgba(5, 12, 31, 0.3);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

:deep(.usa-map-tooltip) {
  &::after {
    position: absolute;
    bottom: -7px;
    left: 50%;
    width: 0px;
    height: 0px;
    border-top: 7px solid #fff;
    border-right: 7px solid transparent;
    border-left: 7px solid transparent;
    content: '';
    transform: translateX(-50%);
  }
}

html[dir='rtl'] .state-count {
  left: 0px;
  right: auto;
}
</style>
